import { useCallback, useEffect, useState } from 'react';
import {
  getHealth,
  getTimeline,
  listAnomalies,
  listExpected,
  listMovements,
  listPortCalls,
  resolveAnomaly,
  setAccessToken,
  submitCorrection,
  type Anomaly,
  type ExpectedTrailer,
  type Health,
  type Movement,
  type PortCall,
  type TimelineEntry,
} from './api.js';
import { estEstimation, heure, libelleSource, resumer } from './previsionnel.js';
import {
  formaterCoordonnees,
  libellePrecision,
  lienCarte,
  positionDepuisChamps,
  type Position,
} from './position.js';
import { oublierSession, seConnecter, sessionEnregistree } from './auth.js';
import { Bulle } from './bulle.jsx';
import { BasculeLangue, Riche, useT, useTErreur } from './i18n.jsx';
import { Timeline } from './timeline.jsx';

/**
 * Interface d'administration (§20).
 *
 * Elle sert à COMPRENDRE, pas à piloter : consulter les mouvements consolidés,
 * remonter à leurs sources, traiter les anomalies et corriger ce qui doit
 * l'être. Une correction n'écrase jamais rien — elle s'ajoute et survit au
 * recalcul.
 */

type Tab = 'movements' | 'previsionnel' | 'anomalies' | 'health';

const IDENTIFICATION_LABELS: Record<string, string> = {
  confirmed: 'Confirmée',
  qr_only: 'QR seul',
  external_only: 'Opérateur seul',
  conflict: 'Conflit',
  unidentified: 'Non identifiée',
};

/**
 * La position en cellule de tableau : coordonnées cliquables, précision en
 * dessous. Le lien `geo:` ouvre l'application de cartographie du poste ou du
 * téléphone — sur un ordinateur de bureau il peut ne rien faire, et c'est
 * acceptable : les coordonnées restent lisibles et copiables.
 */
function CellulePosition({ position, libelle }: { position: Position | null; libelle: string }) {
  const t = useT();
  if (position === null) return <td className="muted">—</td>;
  return (
    <td>
      <a href={lienCarte(position, libelle)} title={t('Ouvrir dans une carte')}>
        {formaterCoordonnees(position)}
      </a>
      <div className={position.reliable ? 'muted' : 'alert'}>{t(libellePrecision(position))}</div>
    </td>
  );
}

export function App(): JSX.Element {
  const t = useT();
  const tErreur = useTErreur();
  const [tab, setTab] = useState<Tab>('movements');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [identite, setIdentite] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [connexionEnCours, setConnexionEnCours] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [movements, setMovements] = useState<Movement[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);

  /*
   * PRÉVISIONNEL. La ressource est la MÊME que celle servie à UNICA — même vue,
   * même assemblage, autre porte. L'exploitant doit pouvoir regarder ce que le
   * diffuseur voit : sans cet écran, il fallait un terminal, et personne ne le
   * fait.
   */
  const [escales, setEscales] = useState<PortCall[]>([]);
  const [escaleChoisie, setEscaleChoisie] = useState<string | null>(null);
  const [attendues, setAttendues] = useState<ExpectedTrailer[]>([]);
  const [health, setHealth] = useState<Health | null>(null);

  const [selected, setSelected] = useState<Movement | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [reviewOnly, setReviewOnly] = useState(false);

  const [correctionValue, setCorrectionValue] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');

  const run = useCallback(async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erreur inconnue');
    }
  }, []);

  // La garde porte sur `authenticated`, un ÉTAT React, et non sur le jeton, qui
  // vit dans une variable de module. Un jeton posé hors de React ne provoque
  // aucun rendu : l'effet de chargement se déclenchait donc une seule fois, au
  // premier rendu, alors que la session n'était pas encore restaurée — et ne se
  // relançait jamais. Il fallait changer d'onglet pour que la liste apparaisse.
  const refresh = useCallback((): void => {
    if (!authenticated) return;
    void run(async () => {
      if (tab === 'movements') {
        const page = await listMovements(reviewOnly ? { requires_manual_review: 'true' } : {});
        setMovements(page.data);
      } else if (tab === 'previsionnel') {
        // Les escales d'abord : sans elles il n'y a rien à choisir, et la
        // première portant un manifeste est celle qu'on veut voir d'emblée.
        const liste = (await listPortCalls()).data;
        setEscales(liste);
        const cible = escaleChoisie ?? liste[0]?.reference ?? null;
        if (cible !== escaleChoisie) setEscaleChoisie(cible);
        setAttendues(cible === null ? [] : (await listExpected(cible)).data);
      } else if (tab === 'anomalies') {
        const page = await listAnomalies({ status: 'open' });
        setAnomalies(page.data);
      } else {
        setHealth(await getHealth());
      }
    });
  }, [run, tab, reviewOnly, authenticated, escaleChoisie]);

  useEffect(refresh, [refresh]);

  /**
   * Rafraîchissement automatique, et onglet caché mis en veille.
   *
   * La liste ne se chargeait qu'au CHANGEMENT d'onglet ou sur le bouton
   * « Rafraîchir » : pendant un débarquement, l'écran restait figé sur l'état
   * qu'il avait à l'ouverture, ce qui ressemble à s'y méprendre à une
   * consolidation qui ne produit rien.
   *
   * Vingt secondes plutôt qu'une : le plan Workers gratuit compte 100 000
   * requêtes par jour, et un onglet laissé ouvert toute la journée en
   * consommerait 4 300 à ce rythme — négligeable, alors qu'à la seconde il en
   * prendrait 86 000 à lui seul. La veille sur onglet caché évite de payer pour
   * un écran que personne ne regarde.
   */
  useEffect(() => {
    if (!authenticated) return undefined;
    const minuteur = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 20_000);
    // Au retour sur l'onglet, on ne fait pas attendre l'utilisateur vingt
    // secondes de plus pour voir ce qui a changé pendant son absence.
    const auRetour = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', auRetour);
    return (): void => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [refresh, authenticated]);

  // Une session encore valable évite de redemander les identifiants à chaque
  // rechargement de l'onglet. Elle meurt avec le navigateur, pas avec la page.
  useEffect(() => {
    const session = sessionEnregistree();
    if (session === null) return;
    setAccessToken(session.accessToken);
    setIdentite(session.email);
    setAuthenticated(true);
  }, []);

  const handleConnect = useCallback((): void => {
    setError(null);
    setConnexionEnCours(true);
    void seConnecter(email.trim(), motDePasse)
      .then((session) => {
        setAccessToken(session.accessToken);
        setIdentite(session.email);
        setAuthenticated(true);
        setMotDePasse('');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Connexion refusée.');
      })
      .finally(() => {
        setConnexionEnCours(false);
      });
  }, [email, motDePasse]);

  const handleDisconnect = useCallback((): void => {
    oublierSession();
    setAccessToken(null);
    setAuthenticated(false);
    setIdentite(null);
  }, []);

  const openMovement = useCallback(
    (movement: Movement): void => {
      setSelected(movement);
      setCorrectionValue(movement.trailer_reference ?? '');
      setCorrectionReason('');
      void run(async () => {
        const result = await getTimeline(movement.movement_id);
        setTimeline(result.data);
      });
    },
    [run],
  );

  if (!authenticated) {
    return (
      <div className="login">
        <img
          className="bandeau"
          src="/bandeau-interreg-easy2log.png"
          alt={t('Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG')}
        />
        <div className="login__entete">
          <h1>easy2log — Administration</h1>
          <BasculeLangue />
        </div>
        <p className="hint">
          {t(
            'Votre mot de passe ne va qu’à Supabase. Les données, elles, sont lues par le serveur avec un rôle dédié — le navigateur ne parle jamais directement à la base.',
          )}
        </p>
        <input
          type="email"
          value={email}
          placeholder={t('Courriel')}
          autoComplete="username"
          onChange={(event): void => setEmail(event.target.value)}
        />
        <input
          type="password"
          value={motDePasse}
          placeholder={t('Mot de passe')}
          autoComplete="current-password"
          onChange={(event): void => setMotDePasse(event.target.value)}
          onKeyDown={(event): void => {
            if (event.key === 'Enter') handleConnect();
          }}
        />
        <button type="button" onClick={handleConnect} disabled={connexionEnCours}>
          {connexionEnCours ? t('Connexion…') : t('Se connecter')}
        </button>
        {error !== null && <p className="error">{tErreur(error)}</p>}
        {/*
          Sur l'écran de connexion AUSSI : c'est là qu'arrive quelqu'un qui ne
          connaît pas l'outil, et le mode d'emploi est précisément ce qu'il lui
          faut avant d'avoir un compte.
        */}
        <p className="login__manuel">
          <a
            className="lien-manuel"
            href="mode-emploi.html"
            target="_blank"
            rel="noopener"
            title={t('S’ouvre dans un nouvel onglet')}
          >
            {t('Mode d’emploi')}
          </a>
        </p>
        <div className="logos">
          <img
            src="/epcic.png"
            alt={t('EPCIC — Établissement public du commerce et de l’industrie de Corse')}
          />
          <img
            src="/unica.png"
            alt={t('UNICA — Università degli Studi di Cagliari, chef de file')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <header>
        {/* Le bandeau tient lieu de titre : il porte déjà « EASY2LOG ». */}
        <img
          className="bandeau"
          src="/bandeau-interreg-easy2log.png"
          alt={t('Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG')}
        />
        <nav>
          {(['movements', 'previsionnel', 'anomalies', 'health'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={tab === value ? 'active' : ''}
              onClick={(): void => {
                setTab(value);
                setSelected(null);
              }}
            >
              {value === 'movements'
                ? t('Mouvements')
                : value === 'previsionnel'
                  ? t('Prévisionnel')
                  : value === 'anomalies'
                    ? t('Anomalies')
                    : t('Santé')}
            </button>
          ))}
        </nav>
        <button type="button" onClick={refresh}>
          {t('Rafraîchir')}
        </button>
        <span className="identite">{identite ?? ''}</span>
        <a
          className="lien-manuel"
          href="mode-emploi.html"
          target="_blank"
          rel="noopener"
          title={t('S’ouvre dans un nouvel onglet')}
        >
          {t('Mode d’emploi')}
        </a>
        <BasculeLangue />
        <button type="button" onClick={handleDisconnect}>
          {t('Se déconnecter')}
        </button>
      </header>

      {error !== null && <p className="error">{tErreur(error)}</p>}

      {tab === 'movements' && (
        <div className="split">
          <section className="list">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={reviewOnly}
                onChange={(event): void => setReviewOnly(event.target.checked)}
              />
              {t('À revoir uniquement')}
            </label>
            <table>
              <thead>
                <tr>
                  <th>{t('Référence')}</th>
                  <th>{t('Remorque')}</th>
                  <th>{t('Identification')}</th>
                  <th>{t('Position')}</th>
                  <th>{t('Place')}</th>
                  <th>{t('Conf.')}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr
                    key={movement.movement_id}
                    className={`${selected?.movement_id === movement.movement_id ? 'selected' : ''} ${movement.requires_manual_review ? 'review' : ''}`}
                    onClick={(): void => openMovement(movement)}
                  >
                    <td>
                      {movement.movement_reference}
                      {movement.is_simulated && <span className="badge">{t('simulé')}</span>}
                    </td>
                    <td>{movement.trailer_reference ?? '—'}</td>
                    <td className={`ident ident-${movement.identification_status}`}>
                      {t(
                        IDENTIFICATION_LABELS[movement.identification_status] ??
                          movement.identification_status,
                      )}
                    </td>
                    <CellulePosition
                      position={positionDepuisChamps(movement)}
                      libelle={movement.trailer_reference ?? movement.movement_reference}
                    />
                    <td className="muted">{movement.parking_slot_code ?? '—'}</td>
                    <td>
                      {movement.confidence_score === null
                        ? '—'
                        : movement.confidence_score.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {movements.length === 0 && <p className="hint">{t('Aucun mouvement.')}</p>}
          </section>

          <section className="detail">
            {selected === null ? (
              <p className="hint">{t('Sélectionner un mouvement pour voir sa chronologie.')}</p>
            ) : (
              <>
                <h2>{selected.movement_reference}</h2>
                <dl className="stats">
                  <dt>{t('Tracteur')}</dt>
                  <dd>{selected.tractor_reference ?? '—'}</dd>
                  <dt>QR</dt>
                  <dd>{selected.identified_by_qr ? t('oui') : t('non')}</dd>
                  <dt>{t('Opérateur')}</dt>
                  <dd>{selected.identified_by_external_source ? t('oui') : t('non')}</dd>
                  <dt>{t('Motifs')}</dt>
                  <dd>{selected.reason_codes.join(', ') || '—'}</dd>
                </dl>

                <h3>{t('Chronologie')}</h3>
                <Timeline entries={timeline} />

                {selected.requires_manual_review && (
                  <div className="correction">
                    <h3>
                      {t('Correction manuelle')}
                      <Bulle>
                        {t(
                          'Une correction est une SOURCE, pas une retouche : elle s’ajoute à l’historique et survit au recalcul.',
                        )}
                      </Bulle>
                    </h3>
                    <input
                      type="text"
                      value={correctionValue}
                      placeholder={t('Immatriculation retenue')}
                      onChange={(event): void => setCorrectionValue(event.target.value)}
                    />
                    <textarea
                      value={correctionReason}
                      placeholder={t('Motif (obligatoire) — il sera relu dans six mois')}
                      rows={3}
                      onChange={(event): void => setCorrectionReason(event.target.value)}
                    />
                    <button
                      type="button"
                      disabled={correctionReason.trim().length < 3}
                      onClick={(): void => {
                        void run(async () => {
                          await submitCorrection({
                            correction_type: 'identification',
                            movement_id: selected.movement_id,
                            previous_value: { trailer_reference: selected.trailer_reference },
                            new_value: { trailer_reference: correctionValue },
                            reason: correctionReason,
                          });
                          setCorrectionReason('');
                          refresh();
                        });
                      }}
                    >
                      {t('Enregistrer la correction')}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {tab === 'previsionnel' && (
        <section className="list">
          <p className="hint">
            <Riche cle="diffuseur">
              <strong>Ce que le diffuseur voit</strong> — même donnée qu’UNICA, servie par la même
              vue.
            </Riche>
          </p>

          <label className="checkbox">
            Escale&nbsp;
            <select
              value={escaleChoisie ?? ''}
              onChange={(event): void => {
                setEscaleChoisie(event.target.value === '' ? null : event.target.value);
              }}
            >
              {escales.map((e) => (
                <option key={e.reference} value={e.reference}>
                  {e.reference}
                  {e.ship_name === null ? '' : ` — ${e.ship_name}`}
                </option>
              ))}
            </select>
          </label>

          {attendues.length === 0 ? (
            <p className="hint">
              Aucune remorque annoncée pour cette escale. Le prévisionnel vient du plan de
              chargement : sans manifeste importé, il n&apos;y a rien à annoncer.
            </p>
          ) : (
            <>
              {(() => {
                const r = resumer(attendues);
                return (
                  <dl className="stats">
                    <dt>{t('Annoncées')}</dt>
                    <dd>{r.total}</dd>
                    <dt>{t('Sorties')}</dt>
                    <dd>{r.sorties}</dd>
                    <dt>{t('Encore attendues')}</dt>
                    <dd>{r.attendues}</dd>
                    <dt>{t('En retard')}</dt>
                    <dd className={r.enRetard > 0 ? 'alert' : undefined}>{r.enRetard}</dd>
                    <dt>{t('Prochaine')}</dt>
                    <dd>
                      {r.prochaine === null
                        ? '—'
                        : t('{remorque} vers {heure}', {
                            remorque: r.prochaine.trailer_reference,
                            heure: heure(r.prochaine.available_at),
                          })}
                    </dd>
                  </dl>
                );
              })()}

              <table>
                <thead>
                  <tr>
                    <th>{t('Rang')}</th>
                    <th>{t('Remorque')}</th>
                    <th>{t('Disponible')}</th>
                    {/*
                      L'explication est ici, sur la colonne qu'elle explique,
                      plutôt qu'en tête d'onglet : c'est en lisant « estimée »
                      qu'on se demande d'où sort l'heure.
                    */}
                    <th>
                      {t('Origine de l’heure')}
                      <Bulle>
                        <Riche cle="origine-heure">
                          L’heure est <em>constatée</em> une fois la remorque posée, et{' '}
                          <em>estimée</em> avant — sur la cadence réellement mesurée pendant le
                          débarquement, ou sur la cadence de référence tant que rien n’est sorti.
                        </Riche>
                      </Bulle>
                    </th>
                    <th>{t('Position')}</th>
                    <th>{t('Place')}</th>
                  </tr>
                </thead>
                <tbody>
                  {attendues.map((a) => (
                    <tr
                      key={a.trailer_id}
                      className={a.is_overdue ? 'review' : undefined}
                    >
                      <td>{a.predicted_discharge_rank}</td>
                      <td>{a.trailer_reference}</td>
                      <td className={a.is_overdue ? 'alert' : undefined}>
                        {heure(a.available_at)}
                        {a.is_overdue && ` — ${t('en retard')}`}
                      </td>
                      <td className={estEstimation(a.source) ? 'muted' : undefined}>
                        {t(libelleSource(a.source))}
                      </td>
                      <CellulePosition position={a.position} libelle={a.trailer_reference} />
                      <td className="muted">
                        {a.parking_slot_code === null
                          ? '—'
                          : `${a.parking_area_code ?? '?'} / ${a.parking_slot_code}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}

      {tab === 'anomalies' && (
        <section className="list wide">
          <table>
            <thead>
              <tr>
                <th>{t('Détectée')}</th>
                <th>{t('Code')}</th>
                <th>{t('Sévérité')}</th>
                <th>{t('Mouvement')}</th>
                <th>{t('Détail')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {anomalies.map((anomaly) => (
                <tr key={anomaly.anomaly_id}>
                  <td>{anomaly.detected_at.replace('T', ' ').slice(0, 19)}</td>
                  <td>
                    <code>{anomaly.anomaly_code}</code>
                  </td>
                  <td className={`sev sev-${anomaly.severity}`}>{anomaly.severity}</td>
                  <td>{anomaly.movement_reference ?? '—'}</td>
                  <td className="details">{JSON.stringify(anomaly.details)}</td>
                  <td>
                    <button
                      type="button"
                      onClick={(): void => {
                        void run(async () => {
                          await resolveAnomaly(anomaly.anomaly_id, 'acknowledged', '');
                          refresh();
                        });
                      }}
                    >
                      {t('Prendre en compte')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {anomalies.length === 0 && <p className="hint">{t('Aucune anomalie ouverte.')}</p>}
        </section>
      )}

      {tab === 'health' && health !== null && (
        <section className="list wide">
          <h2>{t('Files et traitements')}</h2>
          <dl className="stats">
            <dt>{t('Messages non traitables')}</dt>
            <dd className={health.queues.deadLettersUnresolved > 0 ? 'alert' : 'ok'}>
              {health.queues.deadLettersUnresolved}
            </dd>
            <dt>{t('Dernière observation')}</dt>
            <dd>{health.queues.lastObservationAt ?? '—'}</dd>
            <dt>{t('Observations (1 h)')}</dt>
            <dd>{health.queues.observationsLastHour}</dd>
            <dt>{t('Boîtiers vus (1 h)')}</dt>
            <dd>{health.queues.devicesSeenLastHour}</dd>
            <dt>
              {t('Observations en retard (1 h)')}
              <Bulle>
                {t(
                  'Un pic signale une coupure réseau en cours, pas une panne : les boîtiers rattrapent leur tampon.',
                )}
              </Bulle>
            </dt>
            <dd>{health.queues.lateObservationsLastHour}</dd>
          </dl>

          <h2>
            {t('Dérive d’horloge des boîtiers')}
            <Bulle>
              {t(
                'Tout le rapprochement temporel repose sur l’horloge du boîtier. Une dérive non détectée rend les fenêtres inopérantes sans que rien ne le signale.',
              )}
            </Bulle>
          </h2>
          <table>
            <thead>
              <tr>
                <th>{t('Boîtier')}</th>
                <th>{t('Dérive')}</th>
                <th>{t('Mesurée')}</th>
                <th>{t('Dernier contact')}</th>
              </tr>
            </thead>
            <tbody>
              {health.device_clock_drift.map((device) => (
                <tr key={device.deviceId}>
                  <td>{device.deviceId}</td>
                  <td className={Math.abs(device.clockSkewMs ?? 0) > 5000 ? 'alert' : 'ok'}>
                    {device.clockSkewMs === null ? '—' : `${String(device.clockSkewMs)} ms`}
                  </td>
                  <td>{device.clockSkewAt ?? '—'}</td>
                  <td>{device.lastSeenAt ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {/*
        Le bandeau est en PIED de page, pas dans la barre d'outils : il lui faut
        environ 300 px pour que « Cofinancé par l'Union européenne » se lise, et
        la barre n'a pas cette place — il la faisait déborder, le bouton « Se
        déconnecter » passait hors écran. Une attribution de financement
        illisible ou tronquée ne vaut pas mieux qu'une attribution absente.
      */}
      <footer className="attribution">
        <div className="logos">
          <img
            src="/epcic.png"
            alt={t('EPCIC — Établissement public du commerce et de l’industrie de Corse')}
          />
          <img
            src="/unica.png"
            alt={t('UNICA — Università degli Studi di Cagliari, chef de file')}
          />
        </div>
      </footer>
    </div>
  );
}
